<template>
  <div class="flex flex-col h-full">
    <!-- Day header row -->
    <div class="flex flex-shrink-0 bg-white dark:bg-[#1C2128] sticky top-0 z-10" :class="hasAllDayBookings || hasTasksRow ? '' : 'border-b border-gray-200 dark:border-gray-700'">
      <!-- Time gutter — timezone labels -->
      <div class="flex-shrink-0 flex items-end pb-1" :class="showSecondary ? 'w-24' : 'w-14'">
        <div v-if="showSecondary" class="flex-1 flex items-end justify-end pr-1">
          <span class="text-[11px] font-semibold text-indigo-400 leading-none">
            {{ secondaryTzLabel }}
          </span>
        </div>
        <div class="w-14 flex-shrink-0 flex items-end justify-end pr-2">
          <span v-if="primaryTzLabel"
                class="text-[11px] font-semibold text-gray-600 dark:text-gray-400 leading-none">
            {{ primaryTzLabel }}
          </span>
        </div>
      </div>

      <div
        v-for="day in weekDays"
        :key="day.key"
        class="text-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        :class="settingsStore.narrow_weekend && day.isWeekend ? 'flex-none w-12' : 'flex-1 min-w-0'"
        @click="emit('day-click', day.date)"
      >
        <p class="text-[13px] font-medium tracking-wide uppercase"
          :class="day.isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'"
        >
          {{ day.weekdayShort }}
        </p>
        <div
          class="w-10 h-10 mx-auto mt-0.5 flex items-center justify-center rounded-full text-[24px] transition-colors"
          :class="day.isToday
            ? 'bg-blue-600 text-white font-semibold'
            : 'text-gray-700 dark:text-gray-300 font-normal hover:bg-gray-100 dark:hover:bg-gray-700'"
        >
          {{ day.dayNum }}
        </div>
      </div>
    </div>

    <!-- All-day events row -->
    <div
      v-if="hasAllDayBookings || hasTasksRow"
      class="flex flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]"
    >
      <div class="flex-shrink-0" :class="showSecondary ? 'w-24' : 'w-14'"></div>
      <div
        v-for="day in weekDays"
        :key="day.key"
        class="min-w-0 border-l border-gray-100 dark:border-gray-800 p-0.5 min-h-[26px] space-y-0.5"
        :class="settingsStore.narrow_weekend && day.isWeekend ? 'flex-none w-12' : 'flex-1 min-w-0'"
      >
        <div
          v-for="booking in allDayBookingsForDay(day)"
          :key="booking.id"
          class="text-[12px] font-medium truncate px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
          :style="{
            backgroundColor: darkenColor(booking.color_code || '#3b82f6'),
            color: readableTextColor(booking.color_code || '#3b82f6'),
          }"
          @click="emit('event-click', booking, $event.currentTarget.getBoundingClientRect())"
        >
          {{ booking.title }}
        </div>
        <CalendarTaskSummary
          :tasks="tasksForDay(day.date)"
          @open="openTask"
        />
      </div>
    </div>

    <!-- Scrollable time grid -->
    <div class="flex-1 overflow-y-auto" ref="gridEl">
      <div class="flex" style="height: 1536px;">
        <!-- Hour label column (primary + optional secondary TZ) -->
        <div class="flex-shrink-0 flex" :class="showSecondary ? 'w-24' : 'w-14'">
          <!-- Secondary TZ labels -->
          <div v-if="showSecondary" class="relative w-10">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-1"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-indigo-400">
                {{ secondaryHourLabels[hour - 1] }}
              </span>
            </div>
          </div>
          <!-- Primary TZ labels -->
          <div class="relative w-14">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-2"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-gray-600 dark:text-gray-400">
                {{ hourLabels[hour - 1] }}
              </span>
            </div>
          </div>
        </div>

        <!-- Day columns -->
        <div
          v-for="day in weekDays"
          :key="day.key"
          :data-day-key="day.dayKey"
          class="relative border-l border-gray-200 dark:border-gray-800 touch-none"
          :class="[
            day.isToday ? 'bg-blue-50/30 dark:bg-blue-950/10' : 'bg-white dark:bg-[#0d1117]',
            settingsStore.narrow_weekend && day.isWeekend ? 'flex-none w-12' : 'flex-1 min-w-0',
          ]"
          @click="onSlotClick(day.date, $event)"
          @pointerdown="(e) => onGridPointerDown(e, day)"
        >
          <!-- Hour grid lines -->
          <div
            v-for="hour in 24"
            :key="hour"
            class="absolute left-0 right-0 border-t border-gray-200 dark:border-gray-800"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT}px` }"
          ></div>

          <!-- Half-hour dashed lines -->
          <div
            v-for="hour in 24"
            :key="`h-${hour}`"
            class="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-900"
            style="border-style: dashed;"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT + ROW_HEIGHT / 2}px` }"
          ></div>

          <!-- Current time indicator (today column only) -->
          <CalendarCurrentTimeLine v-if="day.isToday && settingsStore.show_now_indicator" />

          <!-- Event cards — one per day-segment, positioned side by side -->
          <CalendarEventCard
            v-for="segment in segmentsForDay(day)"
            :dragging="gridDrag.isBeingDragged(segment.booking.id)"
            :key="segment.key"
            :segment="segment"
            :position="positionFor(day, segment)"
            @click="(booking, rect) => !gridDrag.justDragged() && emit('event-click', booking, rect)"
            @drag-start="(e, mode, bk, seg) => gridDrag.start(e, mode, { booking: bk, segment: seg, column: columnFor(day) })"
            @move-blocked="(reason) => emit('move-blocked', reason)"
          />

          <!-- Live drag preview -->
          <CalendarDragGhost :ghost="gridDrag.ghostFor(day.dayKey)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useRouter } from 'vue-router'
import { useTaskDates }             from 'composables/useTaskDates.js'
import { useCalendarGeometry, ROW_HEIGHT } from 'composables/useCalendarGeometry.js'
import { layoutDayEvents, segmentBookingsByDay, coversAllDay, dedupeAllDay } from 'utils/layoutEvents.js'
import { useGridDrag }      from 'composables/useGridDrag.js'
import CalendarEventCard      from './CalendarEventCard.vue'
import CalendarDragGhost      from './CalendarDragGhost.vue'
import CalendarCurrentTimeLine from './CalendarCurrentTimeLine.vue'
import CalendarTaskSummary     from './CalendarTaskSummary.vue'

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['event-click', 'slot-click', 'day-click', 'reschedule', 'move-blocked'])

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const gridEl        = ref(null)

// All timezone maths, hour labels and colour helpers are shared with the day
// and 4-day views so the three grids cannot drift apart.
const {
  dayKey, minutesOfDay, currentMinutesOfDay, dateAtMinutes, yToMinutes, snapMinutes,
  showSecondary, primaryTzLabel, secondaryTzLabel, hourLabels, secondaryHourLabels,
  darkenColor, readableTextColor,
} = useCalendarGeometry()

// ── Date helpers ─────────────────────────────────────────────────────────────

function weekStart(date) {
  const startDay = Number(globalStore.user?.start_day_of_week ?? 0)
  const d = new Date(date)
  const diff = (d.getDay() - startDay + 7) % 7
  d.setDate(d.getDate() - diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function weekEnd(date) {
  const d = weekStart(date)
  d.setDate(d.getDate() + 6)
  d.setHours(23, 59, 59, 999)
  return d
}

const weekDays = computed(() => {
  const today  = new Date(); today.setHours(0, 0, 0, 0)
  const start  = weekStart(props.selectedDate)
  const days   = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const midnight = new Date(d); midnight.setHours(0, 0, 0, 0)
    return {
      date:         midnight,
      key:          d.toISOString(),
      dayKey:       dayKey(new Date(midnight.getTime() + 12 * 3600000)),
      weekdayShort: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum:       d.getDate(),
      isToday:      midnight.getTime() === today.getTime(),
      isWeekend:    midnight.getDay() === 0 || midnight.getDay() === 6,
    }
  })
  if (settingsStore.show_only_work_week) {
    return days.filter(d => d.date.getDay() !== 0 && d.date.getDay() !== 6)
  }
  return days
})

// ── Bookings for a specific day ───────────────────────────────────────────────

// One pass over the visible bookings splits every event into per-day segments
// (so a booking crossing midnight shows on both days, clamped to each) and lays
// each day out into side-by-side columns.
const segmentsByDay = computed(() =>
  segmentBookingsByDay(store.filteredBookings, { dayKey, minutesOfDay })
)

const layoutByDay = computed(() => {
  const result = new Map()
  for (const [key, segments] of segmentsByDay.value) {
    const positions = layoutDayEvents(segments)
    result.set(key, new Map(positions.map(p => [p.key, p])))
  }
  return result
})

function segmentsForDay(day) {
  return segmentsByDay.value.get(day.dayKey) ?? []
}

function positionFor(day, segment) {
  return layoutByDay.value.get(day.dayKey)?.get(segment.key)
}

const allDayBookings = computed(() => store.filteredBookings.filter(b => b.all_day))

// Matched on calendar dates, not instants: all-day events carry an exclusive
// end date, and comparing them against a column's local midnight rendered them
// a day too long for anyone away from UTC.
function allDayBookingsForDay(day) {
  // Collapsed per day: the same holiday subscribed on several calendars is one
  // event to a reader, and stacking every copy buries the real ones.
  return dedupeAllDay(allDayBookings.value.filter(b => coversAllDay(b, day.dayKey)))
}

const hasAllDayBookings = computed(() =>
  allDayBookings.value.length > 0 && weekDays.value.some(d => allDayBookingsForDay(d).length > 0)
)

// ── Tasks ─────────────────────────────────────────────────────────────────────

const router = useRouter()
const { dateStrInUserTz } = useTaskDates()

function cellDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function tasksForDay(dayMidnight) {
  const key = cellDateStr(dayMidnight)
  return store.filteredTasks.filter(t => dateStrInUserTz(t.due_at) === key)
}

const hasTasksRow = computed(() => weekDays.value.some(d => tasksForDay(d.date).length > 0))

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}


// ── Slot click (empty area) ───────────────────────────────────────────────────

function onSlotClick(dayDate, event) {
  // A drag that ended on this column also fires a click; the drag has already
  // been handled, so swallow it rather than opening the create form on top.
  if (gridDrag.isDragging.value || gridDrag.justDragged()) return
  const rect = event.currentTarget.getBoundingClientRect()
  emit('slot-click', dateAtMinutes(dayDate, snapMinutes(yToMinutes(event.clientY - rect.top))))
}

// ── Drag, drop and resize ─────────────────────────────────────────────────────

// Column elements are read straight out of the DOM by their data-day-key rather
// than tracked through v-for refs: the ref callbacks are re-invoked on every
// re-render (a new arrow each time), which made the lookup racy exactly while a
// drag was re-rendering the grid.
function columnEl(dayKey) {
  return gridEl.value?.querySelector(`[data-day-key="${dayKey}"]`) ?? null
}

function columnFor(day) {
  return { dayKey: day.dayKey, date: day.date, el: columnEl(day.dayKey) }
}

// A pointerdown anywhere in the column that is not on an event chip starts a
// create drag. This used to use the .self modifier, but the column contains 48
// absolutely-positioned hour and half-hour rules that swallow the hit.
function onGridPointerDown(event, day) {
  if (event.target.closest('[data-event-chip]')) return
  gridDrag.start(event, 'create', { column: columnFor(day) })
}

const gridDrag = useGridDrag({
  dayColumns: () => weekDays.value.map(columnFor),
  scrollEl:   () => gridEl.value,
  onCreate:   ({ column, startMinutes, endMinutes }) => {
    emit('slot-click', dateAtMinutes(column.date, startMinutes), dateAtMinutes(column.date, endMinutes))
  },
  onCommit:   ({ booking, column, startMinutes, endMinutes }) => {
    emit('reschedule', booking, { dayKey: column.dayKey, startMinutes, endMinutes })
  },
})

// ── Data fetching ─────────────────────────────────────────────────────────────

// Local calendar dates, not UTC: toISOString() shifts the day either side of
// UTC midnight, which made the requested range disagree with the visible week.
function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

async function fetchWeek(date) {
  const start = isoDate(weekStart(date))
  const end   = isoDate(weekEnd(date))
  await Promise.all([store.fetchBookings(start, end), store.fetchTasks(start, end)])
}

onMounted(async () => {
  await Promise.all([globalStore.fetchCurrentUser(), fetchWeek(props.selectedDate)])
  if (gridEl.value) {
    gridEl.value.scrollTop = Math.max(0, (currentMinutesOfDay() / 60) * ROW_HEIGHT - 2 * ROW_HEIGHT)
  }
})

watch(() => props.selectedDate, (newDate) => fetchWeek(newDate))
</script>
